<template>
    <div :class="cardClass">
        <template v-if="!isLoading">
            <div
                v-if="showHeaderBar"
                class="TableCard__header-bar"
            >
                <div class="TableCard__header">
                    <!-- @slot Can be used in place of the `tableHeader` prop -->
                    <slot name="headerText">
                        <span
                            v-if="tableHeader !== ''"
                            class="TableCard__header-text"
                        >
                            {{ tableHeader }}
                        </span>
                    </slot>
                    <div
                        class="TableCard__table-filters"
                    >
                        <!-- @slot Display custom table filters in the table header -->
                        <slot name="tableFilters"></slot>
                    </div>
                </div>
                <div
                    class="TableCard__header-actions"
                >
                    <template v-if="!isLoading">
                        <!-- @slot Display custom actions in the table header -->
                        <slot name="headerActions"></slot>
                    </template>
                </div>
            </div>
            <!--
                See RPDatatable
                @event set-sort
            -->
            <!--
                See RPDatatable
                @event input
            -->
            <!--
                See RPDatatable
                @event row-select
            -->
            <RPDatatable
                :value="value"
                :headers="headers"
                :table-data="tableData"
                :sort="sort"
                :sort-direction="sortDirection"
                :hide-id="hideId"
                :show-action-cell="showActionCell"
                :show-extra-options="showExtraOptions"
                :status="tableStatus"
                table-type="card"
                @set-sort="s => $emit('set-sort', s)"
                @input="v => $emit('input', v)"
                @change="v => $emit('change', v)"
                @row-select="row => $emit('row-select', row)"
            >
                <template
                    v-for="header in headers"
                    v-slot:[header.key]="{prop, rowData}"
                >
                    <!--
                        @slot See RPDatatable
                    -->
                    <slot
                        :name="header.key"
                        :prop="prop"
                        :rowData="rowData"
                    >
                        {{ rowData[prop] }}
                    </slot>
                </template>
                <template v-slot:actionSlot="{ rowId }">
                    <!-- @slot See RPDatatable -->
                    <slot
                        :rowId="rowId"
                        name="actionSlot"
                    >
                    </slot>
                </template>
                <template v-slot:extraSlot="{ rowId, rowData}">
                    <slot
                        :rowId="rowId"
                        :rowData="rowData"
                        name="extraSlot"
                    >
                    </slot>
                </template>
                <template v-slot:extraOptions="{ rowId, rowData }">
                    <!-- @slot See RPDatatable -->
                    <slot
                        :rowId="rowId"
                        :rowData="rowData"
                        name="extraOptions"
                    >
                    </slot>
                </template>
            </RPDatatable>
            <!--
                Prompt to navigate to particular page in table data
                @event navToPage
                @property {object} page information
            -->
            <Pagination
                v-if="showPagination"
                :pagination="pagination"
                :items="items"
                :show-total="showTotal"
                :status="status"
                @navToPage="p => $emit('navToPage', p)"
            />
            <slot
                v-if="!isLoading"
                name="additional"
            ></slot>
        </template>
    </div>
</template>
<script>
import RPDatatable from '../RPDatatable/RPDatatable.vue';
import Pagination from '../Pagination/Pagination.vue';
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import has from 'lodash/has';

export default {
    name: 'RPTableCard',
    components: {
        RPDatatable,
        Pagination
    },
    props: {
        /**
         * See RPDatatable
         * @model
         */
        value: {
            type: [Array, String, Number],
            default: () => null,
        },
        /**
         * See RPDatatable
         */
        headers: {
            type: Array,
            required: true,
        },
        /**
         * See RPDatatable
         */
        tableData: {
            type: Array,
            required: true,
        },
        /**
         * See RPDatatable
         */
        sort: {
            type: String,
            default: '',
        },
        /**
         * See RPDatatable
         */
        sortDirection: {
            type: String,
            default: 'asc',
            validator: val => ['asc', 'desc'].indexOf(val) !== -1,
        },
        /**
         * See RPDatatable
         */
        hideId: {
            type: Boolean,
            default: false,
        },
        /**
         * See RPDatatable
         */
        showActionCell: {
            type: Boolean,
            default: false,
        },
        /**
         * See RPDatatable
         */
        showExtraOptions: {
            type: Boolean,
            default: false,
        },
        /**
         * Whether to display the card header, including header text,
         * custom filters, and header actions
         */
        showHeaderBar: {
            type: Boolean,
            default: true,
        },
        /**
         * Table header text
         */
        tableHeader: {
            type: String,
            default: ''
        },
        /**
         * The pagination object, with `totalPages` and `currentPage` props.
         */
        pagination: {
            type: Object,
            default: () => ({totalPages: -1, currentPage: -1}),
            validator: (obj) => has(obj, 'totalPages') && has(obj, 'currentPage'),
        },
        /**
         * The `items` prop is used for navigation and includes these keys:
         * `total`, `start`, `end`, `name`
         * TODO: needs refactoring. See Trello
         */
        items: {
            type: Object,
            default: () => ({total: -1, start: -1, end: -1, name: ''}),
            validator: (obj) => has(obj, 'total') && has(obj, 'start') && has(obj, 'end') && has(obj, 'name'),
        },
        /**
         * Whether to display the total number of items
         */
        showTotal: {
            type: Boolean,
            default: false,
        },
        tableStatus: {
            type: String,
            default: stat.STANDBY,
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    computed: {
        isLoading(){
            return this.status === stat.LOADING;
        },
        cardClass(){
            return statusBuilder('TableCard', this.status);
        },
        showPagination(){
            return this.pagination.totalPages > 1;
        },
    },

};
</script>
<style lang="scss">
    .TableCard {
        background: $white;
        width: 100%;
        border-radius: 4px;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
        padding: 1.5rem;
        border: 1px solid transparent;
        margin-bottom: 1rem;

        @include loading(25rem, 100%);

        &__header-bar {
            display:flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
        }
        &__header{
            display: flex;
            align-items: center;
        }
        &__header-text {
            font-weight: 600;
            margin-right: 1rem;
        }
        &__header-actions {
            display: flex;
            align-items: center;
            & > * {
                margin-left: 1rem;
            }
        }
    }
</style>

<docs>
```[import](./RPTableCardExample.vue)
```
</docs>
